import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faTimes } from "@fortawesome/free-solid-svg-icons";
import AOS from 'aos';
import 'aos/dist/aos.css';

function HeaderSearch() {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showDrop, setShowDrop] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setShowDrop(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim() || query.length < 2) { setSuggestions([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5&countrycodes=gb`,
          { headers: { 'Accept-Language': 'en' } }
        );
        const data = await res.json();
        setSuggestions(data.map(r => ({
          name: r.name || r.display_name.split(',')[0],
          full: r.display_name,
          lat: r.lat,
          lon: r.lon,
        })));
      } catch { setSuggestions([]); }
      finally { setLoading(false); }
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const go = (q) => {
    window.location.href = `/map?q=${encodeURIComponent(q)}`;
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="flex items-center border border-gray-300 rounded-lg bg-white overflow-hidden">
        <img src="/images/search-icon.png" alt="Search" className="h-5 w-5 ml-3 flex-shrink-0" />
        <input
          type="text"
          value={query}
          placeholder="Search location or city"
          onChange={e => { setQuery(e.target.value); setShowDrop(true); }}
          onFocus={() => query && setShowDrop(true)}
          onKeyDown={e => {
            if (e.key === 'Enter' && query.trim()) { setShowDrop(false); go(query.trim()); }
            if (e.key === 'Escape') setShowDrop(false);
          }}
          className="px-3 py-2 text-sm text-gray-700 bg-transparent focus:outline-none w-48"
        />
        {loading && <div className="w-3 h-3 border-2 border-[#29752B] border-t-transparent rounded-full animate-spin mr-3 flex-shrink-0" />}
      </div>

      {showDrop && suggestions.length > 0 && (
        <div className="absolute top-full mt-1.5 left-0 w-72 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-[99999]">
          {suggestions.map((s, i) => (
            <button
              key={i}
              onMouseDown={e => { e.preventDefault(); setQuery(s.name); setShowDrop(false); go(s.name); }}
              className="w-full text-left flex items-start gap-3 px-4 py-3 hover:bg-[#EEFAF7] transition border-b border-gray-50 last:border-0"
            >
              <i className="fas fa-map-marker-alt text-[#29752B] text-xs mt-1 flex-shrink-0"></i>
              <div className="min-w-0">
                <p className="text-sm font-medium text-[#172A3A] truncate">{s.name}</p>
                <p className="text-xs text-gray-400 truncate">{s.full}</p>
              </div>
            </button>
          ))}
          <div className="px-4 py-2 border-t border-gray-100">
            <button
              onMouseDown={e => { e.preventDefault(); setShowDrop(false); go(query); }}
              className="text-xs text-[#29752B] font-medium hover:underline flex items-center gap-1"
            >
              <i className="fas fa-search text-[10px]"></i>
              Search on map for "{query}"
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const STATUS_COLORS = {
  Pending:   { dot: 'bg-yellow-400', text: 'text-yellow-700 bg-yellow-50' },
  Approved:  { dot: 'bg-green-500',  text: 'text-green-700 bg-green-50'  },
  Rejected:  { dot: 'bg-red-500',    text: 'text-red-700 bg-red-50'      },
  Completed: { dot: 'bg-blue-500',   text: 'text-blue-700 bg-blue-50'    },
};

const Header = ({ isDashboard, isExternal, isAuthenticated, user }) => {
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const dropdownRef = useRef(null);
  const notifRef = useRef(null);

  const handleLogout = () => {
    localStorage.removeItem("token");
    window.location.href = "/login";
  };

  const toggleMobileMenu = () => setMobileMenuOpen(!isMobileMenuOpen);
  const toggleProfileDropdown = () => {
    setShowProfileDropdown(!showProfileDropdown);
    setShowNotifications(false);
  };
  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
    setShowProfileDropdown(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      const heroSectionHeight = document.querySelector(".hero-section")?.offsetHeight || 0;
      setIsScrolled(window.scrollY > heroSectionHeight);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    AOS.init({ duration: 800, once: true, offset: 120 });
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const scrollTo = urlParams.get("scrollTo");
    if (scrollTo) {
      smoothScroll(scrollTo);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowProfileDropdown(false);
      }
      if (notifRef.current && !notifRef.current.contains(e.target)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const smoothScroll = (targetId) => {
    const el = document.getElementById(targetId);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const handleNavigation = (e, targetId, externalPage = false) => {
    e.preventDefault();
    const currentPath = window.location.pathname;
    if (externalPage) {
      window.location.href = `/${targetId}`;
    } else if (currentPath === "/") {
      smoothScroll(targetId);
    } else {
      window.location.href = `/?scrollTo=${targetId}`;
    }
  };

  const currentPath = window.location.pathname;

  const navLinks = [
    { label: "Dashboard", href: "/profile", active: currentPath === "/profile" },
    { label: "Map", href: "/map", active: currentPath === "/map" },
    { label: "How it works", href: "/#how-it-works", active: false },
    { label: "Rewards", href: "/rewards", active: currentPath === "/rewards" },
  ];

  const history = user?.recyclingHistory || [];
  const recentHistory = [...history].reverse().slice(0, 6);
  const pendingCount = history.filter(h => h.status === 'Pending').length;

  return (
    <header
      className={`z-[9999] overflow-visible transition-all duration-300 ${
        isScrolled || isDashboard ? "bg-[#F8FAFB] text-[#172A3A]" : "bg-[#004d3f] text-white"
      } border-b border-solid ${isDashboard ? "" : "sticky top-0"} w-full`}
    >
      <div className="flex lg:mx-12 items-center justify-between px-4 sm:px-6 py-4 sm:py-6">
        {/* Logo */}
        <div className="text-2xl font-bold flex items-center">
          <a href="/">
            <img
              src={isScrolled || isDashboard ? "/images/header_logo.png" : "/images/Second_logo.png"}
              alt="e-save Logo"
              className="h-8 sm:h-10"
              data-aos="fade-right"
              data-aos-delay="100"
            />
          </a>
        </div>

        {/* Desktop Navigation */}
        <ul
          className={`hidden lg:flex space-x-8 items-center ${
            isScrolled || isDashboard ? "text-[#172A3A] text-lg" : "text-lg"
          } flex-grow justify-center`}
        >
          {isAuthenticated ? (
            navLinks.map((link, i) => (
              <li key={link.href}>
                <a
                  href={link.href}
                  data-aos="fade-down"
                  data-aos-delay={`${(i + 1) * 100}`}
                  className={`transition duration-300 ease-in-out py-2 px-4 rounded-full text-base font-medium ${
                    link.active
                      ? "bg-[#EEFAF7] text-[#29752B]"
                      : isScrolled || isDashboard
                        ? "hover:bg-[#D9D9D9] hover:text-[#29752B] text-[#172A3A]"
                        : "hover:bg-[#1B362E]/65 text-white hover:text-yellow-500"
                  }`}
                >
                  {link.label}
                </a>
              </li>
            ))
          ) : (
            <>
              <li>
                <a href="#about" className={`transition duration-300 ease-in-out py-3 px-4 rounded-full ${isScrolled || isDashboard ? "hover:bg-[#D9D9D9] hover:text-[#29752B]" : "hover:bg-[#1B362E]/65 text-white hover:text-yellow-500"}`} onClick={(e) => handleNavigation(e, "about")} data-aos="fade-down" data-aos-delay="100">About e-save</a>
              </li>
              <li>
                <a href="#how-it-works" className={`transition duration-300 ease-in-out py-3 px-4 rounded-full ${isScrolled || isDashboard ? "hover:bg-[#D9D9D9] hover:text-[#29752B]" : "hover:bg-[#1B362E]/65 text-white hover:text-yellow-500"}`} onClick={(e) => handleNavigation(e, "how-it-works")} data-aos="fade-down" data-aos-delay="200">How it works</a>
              </li>
              <li>
                <a href="#benefits" className={`transition duration-300 ease-in-out py-3 px-4 rounded-full ${isScrolled || isDashboard ? "hover:bg-[#D9D9D9] hover:text-[#29752B]" : "hover:bg-[#1B362E]/65 text-white hover:text-yellow-500"}`} onClick={(e) => handleNavigation(e, "benefits")} data-aos="fade-down" data-aos-delay="300">Benefits</a>
              </li>
              <li>
                <a href="#map" className={`transition duration-300 ease-in-out py-3 px-4 rounded-full ${isScrolled || isDashboard ? "hover:bg-[#D9D9D9] hover:text-[#29752B]" : "hover:bg-[#1B362E]/65 text-white hover:text-yellow-500"}`} onClick={(e) => handleNavigation(e, "map")} data-aos="fade-down" data-aos-delay="400">Map</a>
              </li>
            </>
          )}
        </ul>

        {/* Authenticated Desktop Controls */}
        {isAuthenticated ? (
          <div className="hidden lg:flex items-center space-x-3" data-aos="fade-down" data-aos-delay="500">
            <HeaderSearch />

            {/* Notification Bell */}
            <div className="relative" ref={notifRef}>
              <button
                onClick={toggleNotifications}
                className="relative w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition"
                title="Notifications"
              >
                <img src="/images/notification-icon.png" alt="Notification" className="w-5 h-5" />
                {pendingCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                    {pendingCount > 9 ? '9+' : pendingCount}
                  </span>
                )}
              </button>

              {showNotifications && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-xl border border-gray-100 z-[99999] overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                    <h4 className="text-sm font-bold text-[#172A3A]">Recent Activity</h4>
                    {pendingCount > 0 && (
                      <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-medium">
                        {pendingCount} pending
                      </span>
                    )}
                  </div>

                  <div className="max-h-72 overflow-y-auto">
                    {recentHistory.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-10 text-center px-4">
                        <div className="text-3xl mb-2">♻️</div>
                        <p className="text-sm text-gray-500">No recycling activity yet.</p>
                        <a href="/map" className="mt-2 text-xs text-[#29752B] font-medium hover:underline">Find a collection centre →</a>
                      </div>
                    ) : (
                      recentHistory.map((item, i) => {
                        const sc = STATUS_COLORS[item.status] || STATUS_COLORS.Pending;
                        const date = item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '';
                        return (
                          <div key={i} className="flex items-start gap-3 px-4 py-3 hover:bg-gray-50 border-b border-gray-50 last:border-0">
                            <div className={`mt-1 w-2 h-2 rounded-full flex-shrink-0 ${sc.dot}`}></div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-[#172A3A] truncate">
                                {item.deviceType || 'Device'} {item.brandModel ? `· ${item.brandModel}` : ''}
                              </p>
                              <p className="text-[11px] text-gray-400 mt-0.5">
                                {item.quantity || 1}× · {item.deviceCondition || ''} · +{item.pointsEarned || 0} pts
                              </p>
                            </div>
                            <div className="flex flex-col items-end gap-1 flex-shrink-0">
                              <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${sc.text}`}>{item.status}</span>
                              {date && <span className="text-[10px] text-gray-300">{date}</span>}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  <div className="border-t border-gray-100 px-4 py-2.5">
                    <a href="/profile" className="text-xs text-[#29752B] font-medium hover:underline">View full history →</a>
                  </div>
                </div>
              )}
            </div>

            {/* Profile Avatar + Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={toggleProfileDropdown}
                className="w-9 h-9 rounded-full overflow-hidden ring-2 ring-[#29752B] hover:ring-[#004d3f] transition focus:outline-none"
                data-aos="fade-left"
                data-aos-delay="700"
              >
                {user?.profilePicture ? (
                  <img src={user.profilePicture} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <div className="bg-[#A7D3BF] w-full h-full flex items-center justify-center text-[#18421B] font-bold text-sm">
                    {user?.username?.charAt(0)?.toUpperCase() || "?"}
                  </div>
                )}
              </button>

              {showProfileDropdown && (
                <div className="absolute right-0 mt-2 w-52 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-[99999]">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-semibold text-[#172A3A] truncate">{user?.username || "User"}</p>
                    <p className="text-xs text-gray-400 truncate">{user?.email || ""}</p>
                  </div>
                  <div className="py-1">
                    <a
                      href="/settings"
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-[#344054] hover:bg-[#EEFAF7] hover:text-[#29752B] transition"
                    >
                      <i className="fas fa-cog text-xs text-gray-400"></i>
                      Settings
                    </a>
                  </div>
                  <div className="border-t border-gray-100 pt-1">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition"
                    >
                      <i className="fas fa-sign-out-alt text-xs"></i>
                      Log out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="hidden lg:flex space-x-4">
            <a href="/login" className={`transition duration-300 ease-in-out border ${isScrolled ? "border-[#29752B] text-[#29752B]" : "border-blue_gray-100 text-white"} px-4 lg:px-6 py-2 rounded-full ${isScrolled ? "hover:bg-[#D9D9D9] hover:text-[#96d9c9] hover:border-[#96d9c9]" : "hover:text-yellow-500 hover:bg-[#1B362E]/65 hover:border-yellow-500"}`} data-aos="fade-left" data-aos-delay="500">Login</a>
            <a href="/register" className={`transition duration-300 ease-in-out ${isScrolled ? "bg-[#29752B] text-white border-[#29752B]" : "bg-[#1B362E]/65 border border-blue_gray-100 text-white"} px-4 lg:px-6 py-2 rounded-full ${isScrolled ? "hover:bg-[#96d9c9]" : "hover:text-yellow-500 hover:bg-transparent hover:border-yellow-500"}`} data-aos="fade-left" data-aos-delay="600">Register</a>
          </div>
        )}

        {/* Mobile Menu Icon */}
        <div className="lg:hidden ml-auto flex items-center gap-2">
          {isAuthenticated && (
            <div className="relative" ref={notifRef}>
              <button
                onClick={toggleNotifications}
                className="relative w-8 h-8 flex items-center justify-center rounded-full"
              >
                <img src="/images/notification-icon.png" alt="Notification" className="w-5 h-5" style={{ filter: isScrolled || isDashboard ? 'none' : 'brightness(0) invert(1)' }} />
                {pendingCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-red-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center">
                    {pendingCount > 9 ? '9+' : pendingCount}
                  </span>
                )}
              </button>
            </div>
          )}
          <button
            className={isScrolled || isDashboard ? "text-gray-800" : "text-white"}
            onClick={toggleMobileMenu}
          >
            <FontAwesomeIcon icon={isMobileMenuOpen ? faTimes : faBars} size="lg" />
          </button>
        </div>
      </div>

      {/* Mobile Notification Panel */}
      {showNotifications && isAuthenticated && (
        <div className="lg:hidden mx-4 mb-2 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-[99999] relative">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h4 className="text-sm font-bold text-[#172A3A]">Recent Activity</h4>
            {pendingCount > 0 && (
              <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-medium">{pendingCount} pending</span>
            )}
          </div>
          <div className="max-h-60 overflow-y-auto">
            {recentHistory.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-sm text-gray-400">No recycling activity yet.</p>
              </div>
            ) : (
              recentHistory.map((item, i) => {
                const sc = STATUS_COLORS[item.status] || STATUS_COLORS.Pending;
                return (
                  <div key={i} className="flex items-start gap-3 px-4 py-3 border-b border-gray-50 last:border-0">
                    <div className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${sc.dot}`}></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-[#172A3A] truncate">{item.deviceType || 'Device'}</p>
                      <p className="text-[11px] text-gray-400">+{item.pointsEarned || 0} pts</p>
                    </div>
                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${sc.text}`}>{item.status}</span>
                  </div>
                );
              })
            )}
          </div>
          <div className="border-t border-gray-100 px-4 py-2.5">
            <a href="/profile" className="text-xs text-[#29752B] font-medium hover:underline">View full history →</a>
          </div>
        </div>
      )}

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className={`lg:hidden py-4 transition-all duration-300 ${isScrolled || isDashboard ? "bg-[#EBEDF0] text-[#172A3A]" : "bg-[#004d3f] text-white"}`}>
          <nav className="flex flex-col items-center space-y-4">
            {!isAuthenticated && (
              <>
                <a href="/#about" className={`transition duration-300 ease-in-out py-3 px-4 rounded-full ${isScrolled || isDashboard ? "hover:bg-[#D9D9D9]" : "hover:bg-[#1B362E]/65 text-white"}`} onClick={(e) => { handleNavigation(e, "about"); toggleMobileMenu(); }}>About e-save</a>
                <a href="/#how-it-works" className={`transition duration-300 ease-in-out py-3 px-4 rounded-full ${isScrolled || isDashboard ? "hover:bg-[#D9D9D9]" : "hover:bg-[#1B362E]/65 text-white"}`} onClick={(e) => { handleNavigation(e, "how-it-works"); toggleMobileMenu(); }}>How it works</a>
                <a href="#benefits" className={`transition duration-300 ease-in-out py-3 px-4 rounded-full ${isScrolled || isDashboard ? "hover:bg-[#D9D9D9]" : "hover:bg-[#1B362E]/65 text-white"}`} onClick={(e) => { handleNavigation(e, "benefits"); toggleMobileMenu(); }}>Benefits</a>
                <a href="#map" className={`transition duration-300 ease-in-out py-3 px-4 rounded-full ${isScrolled || isDashboard ? "hover:bg-[#D9D9D9]" : "hover:bg-[#1B362E]/65 text-white"}`} onClick={(e) => { handleNavigation(e, "map"); toggleMobileMenu(); }}>Map</a>
              </>
            )}
            {isAuthenticated ? (
              <div className="flex flex-col items-center space-y-3 w-full px-6">
                <div className="flex items-center space-x-3 py-2 border-t border-white/20 w-full justify-center">
                  <div className="w-9 h-9 rounded-full bg-[#A7D3BF] flex items-center justify-center text-[#18421B] font-bold overflow-hidden flex-shrink-0">
                    {user?.profilePicture ? (
                      <img src={user.profilePicture} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      user?.username?.charAt(0)?.toUpperCase() || "?"
                    )}
                  </div>
                  <p className={`text-sm font-medium ${isScrolled || isDashboard ? "text-[#172A3A]" : "text-white"}`}>{user?.username || "User"}</p>
                </div>
                {navLinks.map((link) => (
                  <a key={link.href} href={link.href} className={`w-full text-center py-2 px-4 rounded-full text-sm ${link.active ? "bg-[#29752B] text-white" : `${isScrolled || isDashboard ? "hover:bg-[#D9D9D9] text-[#172A3A]" : "hover:bg-[#1B362E]/65 text-white"}`}`}>
                    {link.label}
                  </a>
                ))}
                <button onClick={handleLogout} className="w-full py-2 px-4 rounded-full text-sm bg-red-500 text-white hover:bg-red-600">
                  Log out
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center space-y-2">
                <a href="/login" className={`transition duration-300 ease-in-out border w-full text-center ${isScrolled ? "border-[#29752B] text-[#29752B]" : "border-blue_gray-100 text-white"} px-6 py-2 rounded-full`}>Login</a>
                <a href="/register" className={`transition duration-300 ease-in-out w-full text-center ${isScrolled ? "bg-[#29752B] text-white border-[#29752B]" : "bg-[#1B362E]/65 border border-blue_gray-100 text-white"} px-6 py-2 rounded-full`}>Register</a>
              </div>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
